import { cloneElement, useMemo, useRef, useState, type FocusEvent, type MouseEvent, type ReactElement } from 'react'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { CitatorSurface, mapCaseStatusToBadgeType } from '@/analytics/enums/citator.enums'
import { trackCitatorBadgeImpression } from '@/analytics/utils/citator-analytics'
import { isDisplayableCaseStatus } from '@/citator/schema/case-status-schema'
import type { AgentV2Reference } from '@/agent-v2/store/schemas'
import { AgentV2CitationSourceTooltipContent } from './AgentV2CitationSourceTooltipContent'
import { pickCitationAnchorRect } from './pick-citation-anchor-rect'

/** The hover tooltip appears after ~1s. */
const kEnterDelayMs = 1000

type AgentV2CitationTooltipProps = {
  reference: AgentV2Reference
  onViewReference: () => void
  onViewCitationAnalysis: () => void
  /** The citation trigger element (legal citation link or numbered badge). */
  children: ReactElement
}

/**
 * Hover tooltip for a chat citation (~1s delay): source title, metadata, and the
 * citator case-status treatment. Deliberately a tooltip, not a popover — nothing
 * toggles on click; clicking the citation opens the source directly. Touch devices
 * never see it (disableTouchListener): with no hover, a tap must mean "open the source".
 */
export function AgentV2CitationTooltip(props: AgentV2CitationTooltipProps) {
  const { reference, onViewReference, onViewCitationAnalysis, children } = props
  const { trackEvent } = useAnalytics()
  const [open, setOpen] = useState(false)

  const caseStatus = reference.source_type === 'caselaw' ? reference.case_status : null
  const displayableBadgeType = isDisplayableCaseStatus(caseStatus) ? mapCaseStatusToBadgeType(caseStatus) : null

  // Impression = treatment visible, i.e. the card opened; once per badge type per
  // element. Only chat renders the card, so the surface is fixed —
  // make it a prop when another surface opts in.
  const lastImpressionBadgeTypeRef = useRef<string | null>(null)
  const handleOpen = () => {
    setOpen(true)
    if (!displayableBadgeType || lastImpressionBadgeTypeRef.current === displayableBadgeType) return
    lastImpressionBadgeTypeRef.current = displayableBadgeType
    trackCitatorBadgeImpression({ trackEvent, surface: CitatorSurface.Chat, badgeType: displayableBadgeType })
  }

  const anchorStateRef = useRef<{ element: HTMLElement | null; pointerY: number | null }>({ element: null, pointerY: null })

  // Popper virtual element resolving to the hovered line fragment (pickCitationAnchorRect).
  // contextElement keeps scroll tracking and the flip boundary on the real trigger.
  const fragmentAnchor = useMemo(
    () => ({
      getBoundingClientRect: () => {
        const { element, pointerY } = anchorStateRef.current
        return element ? pickCitationAnchorRect(element, pointerY) : new DOMRect()
      },
      get contextElement() {
        return anchorStateRef.current.element ?? undefined
      },
    }),
    []
  )

  const recordPointer = (event: MouseEvent<HTMLElement>) => {
    anchorStateRef.current = { element: event.currentTarget, pointerY: event.clientY }
  }
  // MUI composes these with its own trigger handlers on the same cloned child.
  const trigger = cloneElement(children, {
    onMouseOver: (event: MouseEvent<HTMLElement>) => {
      children.props.onMouseOver?.(event)
      recordPointer(event)
    },
    onMouseMove: (event: MouseEvent<HTMLElement>) => {
      children.props.onMouseMove?.(event)
      recordPointer(event)
    },
    onFocus: (event: FocusEvent<HTMLElement>) => {
      children.props.onFocus?.(event)
      anchorStateRef.current = { element: event.currentTarget, pointerY: null }
    },
  })

  return (
    <CustomizableTooltip
      open={open}
      onClose={() => setOpen(false)}
      arrow
      enterDelay={kEnterDelayMs}
      // Without this, MUI drops the delay to 0 for a while after any tooltip opens —
      // sweeping the cursor across citation-dense prose would pop tooltips instantly.
      enterNextDelay={kEnterDelayMs}
      disableTouchListener
      onOpen={handleOpen}
      offset={[0, 0]}
      flipAltBoundary
      popperAnchorEl={fragmentAnchor}
      tooltipClassName="!max-w-none !p-5 !bg-white !rounded-md !text-brand-neutral-950 !font-sans border border-brand-neutral-200 shadow-[0_0.6px_25px_0_rgba(0,0,0,0.10),0_10px_10px_0_rgba(0,0,0,0.04)]"
      arrowClassName="!text-white !w-4 !h-2 [&::before]:border [&::before]:border-brand-neutral-200"
      title={
        <AgentV2CitationSourceTooltipContent
          reference={reference}
          onViewReference={onViewReference}
          onViewCitationAnalysis={onViewCitationAnalysis}
          onDismiss={() => setOpen(false)}
        />
      }
    >
      {trigger}
    </CustomizableTooltip>
  )
}
